import { reactive, watch } from 'vue';
import { isApp, isMobile, isRetina } from '@/core/utils/device';
import Events from '@/core/core/events';
import state from '@/core/core/state';
import options from '@/core/core/options';
import preloaders from '@/core/core/preloaders';
import type { Options as TopNoticeOptions } from '@/components/core/notice/utils';
import type { Style as TopNoticeStyle } from '@/components/core/notice/item/types';

/**
 * Статический класс с текущим состоянимем UI
 *
 * @docs https://ui.topvisor.com/?path=/docs/core-core--docs
 */
export default class Core {

	static state = reactive({ ...state });

	static preloaders = preloaders;

	static widthForMobile = options.widthForMobile;

	static themeName = options.themeName;

	static matchMediaIsMobile?: MediaQueryList;

	static $? = $;

	static Events = Events;

	/**
	 * Различные флаги, необходимые для работы UI
	 */
	static flags = reactive({ needLoadNotice: false });

	static _setState() {
		this.$ = $;

		Core.matchMediaIsMobile = window.matchMedia(`(max-width: ${Core.widthForMobile}px)`);

		Core.state.isMobileUA = isMobile();
		Core.state.isRetina = isRetina();
		Core.state.isApp = isApp();

		Core.useDocumentClassModificators();

		Core.useIsHidden();

		Core.setStateByWindowSize();

		// пересчет значений, зависящих от размеров окна
		// todo Исправить опечатку
		Core.Events.addOnReize(Core.onResize);

		Core.onResize();

		Core.saveToCookie();

		Core.useCtrlClasses();
	}

	private static useDocumentClassModificators() {
		// При наблюдении за Map нет oldValue
		watch(() => [...Core.state.documentClassModificators], (newValue, oldValue) => {
			let classesDeleted = new Set<string>();

			oldValue?.forEach(([_id, className]) => classesDeleted.add(className));
			newValue.forEach(([_id, className]) => classesDeleted.delete(className));

			classesDeleted.forEach((className) => document.documentElement.classList.remove(className));

			newValue.forEach(([_id, className]) => document.documentElement.classList.add(className));
		}, { immediate: true });
	}

	private static useIsHidden() {
		document.addEventListener('visibilitychange', () => {
			Core.state.isHidden = document.hidden;
		});

		Core.state.isHidden = document.hidden;
	}

	private static setStateByWindowSize() {
		Core.state.isMobile = !!Core.matchMediaIsMobile?.matches;
		Core.state.isLandscape = (window.innerWidth > window.innerHeight);
		Core.state.isPortrait = !Core.state.isLandscape;
		Core.state.size = (window.innerWidth > window.innerHeight) ? window.innerWidth : window.innerHeight;

		Core.saveToCookie();
	}

	private static onResize() {
		Core.setStateByWindowSize();
	}

	/**
	 * Сохранить информацию об устройстве в cookie
	 */
	private static saveToCookie() {
		const device = [
			1,
			window.innerWidth,
			window.innerHeight,
			window.devicePixelRatio,
			Number(Core.state.isMobile),
			Number(Core.state.isRetina),
		];

		document.cookie = 'device=' + device.join(',') + '; path=/;';
	}

	/**
	 * Добавить установку класса при нажатии ctrl (cmd) / alt
	 */
	private static useCtrlClasses() {
		const bodyClassList = document.body.classList;

		document.addEventListener('keydown', (e) => {
			if (e.ctrlKey || e.metaKey) bodyClassList.add('top-body-press_ctrl');
			if (e.altKey) bodyClassList.add('top-body-press_alt');
		});

		document.addEventListener('keyup', () => bodyClassList.remove('top-body-press_ctrl', 'top-body-press_alt'));
		document.addEventListener('keypress', () => bodyClassList.remove('top-body-press_ctrl', 'top-body-press_alt'));

		window.addEventListener('blur', () => bodyClassList.remove('top-body-press_ctrl', 'top-body-press_alt'));
	}

	/**
	 * Вывести на экране сообщение в стилизации под push уведомление
	 *
	 * Для удобства работы со словарем, принимает один парамтер `text` и первую строку из него использует как `title`.
	 * Данное поведение можно изменить, передав в параметрах `options.title`
	 *
	 * Для вывода используется компонент TopNotice, требует его подклчючения
	 *
	 * @docs https://ui.topvisor.com/?path=/docs/components-core-notice--docs
	 */
	static notice = async (text: string, style: TopNoticeStyle, options?: TopNoticeOptions) => {
		Core.flags.needLoadNotice = true;

		const noticeComponet = await import('@/components/core/notice/notice.vue');

		return noticeComponet.show(text, style, options);
	};

	/**
	 * Вставить css стили на страницу по ссылке с автоматическим добавлением правил media
	 *
	 * Правила media:
	 * - Файлы *.m.css будут грузиться, только на экранах с шириной менее, чем Core.widthForMobile
	 * - Файлы *.pc.css будут грузиться, только на экранах с шириной более, чем Core.widthForMobile
	 *
	 * Настройкой Core.widthForMobile можно управлять через plugin: @topvisor/ui/plugin/core
	 *
	 * @param fileNames - список ссылок на css файлы
	 * @param useSync - использовать синхронную загрузку css, только для css с того же домена. В синхронном режиме стили предзагружаются независимо от
	 * media
	 */
	static insertCSSLinkToPage = (fileNames: Array<string>, useSync = false) => {
		const cssFilesByOptions = new Map();
		cssFilesByOptions.set('::', []);

		fileNames.forEach(filename => {
			let m = false;
			let pc = false;

			if (filename.match(/\.m\./)) {
				m = true;
			}
			if (filename.match(/\.pc\./)) {
				pc = true;
			}

			const key = `${m}:${pc}`;
			if (!cssFilesByOptions.has(key)) {
				cssFilesByOptions.set(`${m}:${pc}`, []);
			}
			cssFilesByOptions.get(`${m}:${pc}`).push(filename);
		});

		const promises: Array<Promise<unknown>> = [];

		cssFilesByOptions.forEach((fileNames, options: string) => {
			if (!fileNames.length) {
				return;
			}

			const [m, pc] = options.split(':').map(item => item === 'true');

			let media = 'all';
			if (m) {
				media = '(max-width: ' + Core.widthForMobile + 'px)';
			}
			if (pc) {
				media = '(min-width: ' + Core.widthForMobile + 'px)';
			}

			const promisesI = Core._insertToPage(fileNames, media, useSync);
			promisesI.forEach(promise => promises.push(promise));
		});

		return Promise.all(promises);
	};

	private static _insertToPage = (fileNames: Array<string>, media: string, useSync: boolean) => {
		const promises: Array<Promise<unknown>> = [];

		fileNames.forEach(fileName => {
			// синхронная загрузка, чтобы функция не выполнилась раньше загрузки стилей
			if (useSync) {
				const request = new XMLHttpRequest();
				request.open('GET', fileName, false);
				request.send();
			}

			const id = 'topCSSLink-' + fileName.replace(/\W/g, '-');

			// стиль уже загружен
			if (window.hasOwnProperty(id)) return;

			let resolve: Function;
			let reject: Function;
			const promise = new Promise((_resolve, _reject) => {
				resolve = _resolve;
				reject = _reject;
			});
			promises.push(promise);

			const elLink = document.createElement('link');
			elLink.id = id;
			elLink.rel = 'stylesheet';
			elLink.media = media;
			elLink.href = fileName;
			elLink.onload = () => resolve();
			elLink.onerror = () => reject();

			document.head.append(elLink);
		});

		return promises;
	};

	/**
	 * Вставить css стили на страницу с автоматическим добавлением правил media
	 *
	 * Загрузка с Vite:
	 *
	 * ```js
	 * import style from '/style.css?inline';
	 *
	 * insertToPageAsInine(style);
	 * ```
	 *
	 * Загрузка с fetch:
	 *
	 * ```js
	 * const style = await fetch('./style.css');
	 *
	 * insertToPageAsInine(style);
	 * ```
	 *
	 * @param style - css стили в строке
	 * @param type - если указать 'm' или 'pc', то стили будут применяться по условию в зависимости от настройки this.widthForMobile
	 *
	 * @see insertToPage
	 */
	static insertStyleToPage = (style: string, type: '' | 'm' | 'pc' = '') => {
		let media = 'all';

		if (type === 'm') {
			media = '(max-width: ' + Core.widthForMobile + 'px)';
		}

		if (type === 'pc') {
			media = '(min-width: ' + Core.widthForMobile + 'px)';
		}

		const elStyle = document.createElement('style');
		elStyle.innerHTML = style;
		elStyle.media = media;

		document.head.append(elStyle);
	};

}
